import React, { useEffect, useMemo, useState } from "react";
import SEO from "@/components/SEO";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Coins, Plus, Minus, Info } from "lucide-react";
import { customerApi, fmtErr } from "@/customer/api";
import { useCustomerAuth } from "@/customer/AuthContext";

const fmtDate = (iso) => { try { return new Date(iso).toLocaleString("en-ZA", { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; } };

export default function CustomerCredits() {
  const { user, refresh } = useCustomerAuth();
  const [tx, setTx] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    customerApi.get("/customer/credits")
      .then((r) => { if (active) setTx(r.data); })
      .catch((e) => toast.error(fmtErr(e)))
      .finally(() => { if (active) setLoading(false); });
    refresh?.();
    return () => { active = false; };
  }, []);

  const totals = useMemo(() => {
    const added = tx.filter((t) => t.type === "add").reduce((s, t) => s + (t.amount || 0), 0);
    const removed = tx.filter((t) => t.type === "remove").reduce((s, t) => s + (t.amount || 0), 0);
    return { added, removed };
  }, [tx]);

  return (
    <div data-testid="page-customer-credits" className="max-w-[1100px] mx-auto px-5 sm:px-8 pt-32 pb-24 space-y-8">
      <SEO title="CustomerCredits · WelkomBiz Photography" noindex />
      <header className="space-y-3">
        <Link to="/customer" data-testid="customer-credits-back" className="inline-flex items-center gap-2 text-[10px] tracking-luxe uppercase text-white/55 hover:text-white">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to Dashboard
        </Link>
        <p className="text-[11px] tracking-luxe uppercase text-white/45">— Credit History</p>
        <h1 className="font-display text-4xl sm:text-5xl leading-[1.05]">
          <span className="text-chrome">Your credit</span>{" "}
          <span className="italic text-magcyan">activity</span>
          <span className="text-chrome">.</span>
        </h1>
      </header>

      <section className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div data-testid="customer-credits-balance-card" className="relative glass chrome-border rounded-md p-5 overflow-hidden">
          <div className="absolute -top-12 -right-12 w-32 h-32 rounded-full bg-[#ff2e93]/15 blur-2xl pointer-events-none" />
          <p className="text-[10px] tracking-luxe uppercase text-white/45 mb-2">Available Balance</p>
          <p className="font-display text-4xl text-chrome">{user?.credit_balance ?? 0}</p>
        </div>
        <div data-testid="customer-credits-added-card" className="glass chrome-border rounded-md p-5">
          <p className="text-[10px] tracking-luxe uppercase text-white/45 mb-2 inline-flex items-center gap-1"><Plus className="w-3 h-3" /> Total Added</p>
          <p className="font-display text-4xl text-[#7fe6ff]">+{totals.added}</p>
        </div>
        <div data-testid="customer-credits-removed-card" className="glass chrome-border rounded-md p-5">
          <p className="text-[10px] tracking-luxe uppercase text-white/45 mb-2 inline-flex items-center gap-1"><Minus className="w-3 h-3" /> Total Removed</p>
          <p className="font-display text-4xl text-[#ff9fcf]">−{totals.removed}</p>
        </div>
      </section>

      <div className="glass chrome-border rounded-md p-5 flex items-start gap-3" data-testid="customer-credits-info">
        <Info className="w-4 h-4 text-[#18d3ff] mt-1 shrink-0" />
        <p className="text-sm text-white/65 font-body">
          Credits are manually assigned by WelkomBiz Photography after offline payment. They will unlock full-quality photo
          downloads once the Phase 6 checkout goes live. If something looks off, please reach out via Contact Support.
        </p>
      </div>

      <section data-testid="customer-credits-list" className="space-y-3">
        <h2 className="font-display text-2xl sm:text-3xl text-chrome">Transactions</h2>
        {loading ? <p className="text-sm text-white/40">Loading…</p> :
         tx.length === 0 ? (
          <div className="glass chrome-border rounded-md p-10 text-center text-white/55">
            <Coins className="w-6 h-6 mx-auto text-[#ff2e93] mb-3" />
            <p className="text-sm">No credit transactions yet. Once admin assigns credits, they will appear here.</p>
          </div>
         ) : (
          <div className="glass chrome-border rounded-md divide-y divide-white/5 overflow-hidden">
            <div className="hidden sm:grid grid-cols-12 px-5 py-3 bg-white/3 text-[10px] tracking-luxe uppercase text-white/45">
              <div className="col-span-3">Date</div>
              <div className="col-span-2">Type</div>
              <div className="col-span-1 text-right">Amount</div>
              <div className="col-span-2 text-right">Before</div>
              <div className="col-span-2 text-right">After</div>
              <div className="col-span-2 text-right">Reference</div>
            </div>
            {tx.map((t) => (
              <div key={t.id} data-testid={`customer-credit-tx-${t.id}`} className="px-5 py-4 sm:grid sm:grid-cols-12 sm:items-center gap-3">
                <div className="sm:col-span-3 text-white/70 text-sm">{fmtDate(t.created_at)}</div>
                <div className="sm:col-span-2 mt-1 sm:mt-0">
                  <span className={`text-[10px] tracking-luxe uppercase px-2 py-0.5 rounded-full glass-strong border border-white/10 ${t.type === "add" ? "text-[#7fe6ff]" : "text-[#ff9fcf]"}`}>
                    {t.type === "add" ? "Added" : "Removed"}
                  </span>
                </div>
                <div className={`sm:col-span-1 sm:text-right font-display text-xl mt-1 sm:mt-0 ${t.type === "add" ? "text-[#7fe6ff]" : "text-[#ff9fcf]"}`}>
                  {t.type === "add" ? "+" : "−"}{t.amount}
                </div>
                <div className="sm:col-span-2 sm:text-right text-white/55 text-xs mt-1 sm:mt-0">
                  <span className="sm:hidden tracking-luxe uppercase text-white/40 mr-1">Before</span>{t.balance_before ?? "—"}
                </div>
                <div className="sm:col-span-2 sm:text-right text-white text-xs mt-1 sm:mt-0">
                  <span className="sm:hidden tracking-luxe uppercase text-white/40 mr-1">After</span>{t.balance_after ?? "—"}
                </div>
                <div className="sm:col-span-2 sm:text-right text-white/45 text-xs mt-1 sm:mt-0 truncate">
                  {t.reference || "—"}
                </div>
                {(t.customer_note || t.note) && (
                  <div className="sm:col-span-12 text-xs text-white/55 mt-2 italic">“{t.customer_note || t.note}”</div>
                )}
              </div>
            ))}
          </div>
         )
        }
      </section>
    </div>
  );
}
